import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Plus, Search, Edit, Trash2, Building2, MapPin, Phone, Mail,
  X, Save, ChevronDown, ChevronUp, FileText, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Client Form Component
const ClientForm = ({ client, onSave, onCancel, businessId }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    rfc: '',
    address: {
      street: '',
      exterior_number: '',
      interior_number: '',
      neighborhood: '',
      postal_code: '',
      city: '',
      municipality: '',
      state: 'Ciudad de México',
      country: 'México'
    },
    primary_contact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    additional_contacts: [],
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingCP, setLoadingCP] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);

  useEffect(() => {
    if (client) {
      setFormData({
        business_name: client.business_name || '',
        rfc: client.rfc || '',
        address: client.address || {
          street: '',
          exterior_number: '',
          interior_number: '',
          neighborhood: '',
          postal_code: '',
          city: '',
          municipality: '',
          state: 'Ciudad de México',
          country: 'México'
        },
        primary_contact: client.primary_contact || {
          name: '',
          email: '',
          phone: '',
          position: ''
        },
        additional_contacts: client.additional_contacts || [],
        notes: client.notes || ''
      });
    }
  }, [client]);

  const lookupPostalCode = async (cp) => {
    if (cp.length !== 5) return;
    
    setLoadingCP(true);
    try {
      const res = await fetch(`${API}/api/postal-codes/${cp}`);
      const data = await res.json();
      
      if (data.success) {
        setNeighborhoods(data.neighborhoods || []);
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            city: data.city || '',
            municipality: data.municipality || '',
            state: data.state || 'Ciudad de México',
            neighborhood: data.neighborhoods?.[0] || prev.address.neighborhood
          }
        }));
      }
    } catch (error) {
      console.error('Error looking up postal code:', error);
    } finally {
      setLoadingCP(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const url = client?.id 
        ? `${API}/api/clients/${client.id}`
        : `${API}/api/clients`;
      
      const method = client?.id ? 'PUT' : 'POST';
      
      const payload = client?.id ? formData : {
        ...formData,
        business_id: businessId
      };
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
        onSave(data.client);
      }
    } catch (error) {
      console.error('Error saving client:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-400" />
          {client?.id ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos Fiscales */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
            Datos Fiscales
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Razón Social *</label>
              <input
                type="text"
                required
                value={formData.business_name}
                onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nombre de la empresa"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">RFC</label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => setFormData({...formData, rfc: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none uppercase"
                placeholder="RFC de la empresa"
                maxLength={13}
              />
            </div>
          </div>
        </div>

        {/* Dirección */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Dirección
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Calle</label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => setFormData({
                  ...formData, 
                  address: {...formData.address, street: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Núm. Ext.</label>
                <input
                  type="text"
                  value={formData.address.exterior_number}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, exterior_number: e.target.value}
                  })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Núm. Int.</label>
                <input
                  type="text"
                  value={formData.address.interior_number}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, interior_number: e.target.value}
                  })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Código Postal</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.address.postal_code}
                  onChange={(e) => {
                    const cp = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData({
                      ...formData, 
                      address: {...formData.address, postal_code: cp}
                    });
                    if (cp.length === 5) lookupPostalCode(cp);
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="00000"
                  maxLength={5}
                />
                {loadingCP && (
                  <Loader2 className="absolute right-2 top-2.5 w-4 h-4 text-blue-400 animate-spin" />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Colonia</label>
              {neighborhoods.length > 0 ? (
                <select
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, neighborhood: e.target.value}
                  })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                >
                  {neighborhoods.map((n, i) => (
                    <option key={i} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({
                    ...formData, 
                    address: {...formData.address, neighborhood: e.target.value}
                  })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Alcaldía/Municipio</label>
              <input
                type="text"
                value={formData.address.municipality}
                onChange={(e) => setFormData({
                  ...formData, 
                  address: {...formData.address, municipality: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Ciudad</label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => setFormData({
                  ...formData, 
                  address: {...formData.address, city: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Contacto Principal (Atención) */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Atención (Contacto Principal)
            <span className="text-xs text-slate-500 ml-2">• Se usa como "AT'N" en cotizaciones</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nombre</label>
              <input
                type="text"
                value={formData.primary_contact.name}
                onChange={(e) => setFormData({
                  ...formData, 
                  primary_contact: {...formData.primary_contact, name: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                data-testid="client-primary-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Cargo</label>
              <input
                type="text"
                value={formData.primary_contact.position || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  primary_contact: {...formData.primary_contact, position: e.target.value}
                })}
                placeholder="Ej: Director de Operaciones"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                data-testid="client-primary-position-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Correo</label>
              <input
                type="email"
                value={formData.primary_contact.email}
                onChange={(e) => setFormData({
                  ...formData, 
                  primary_contact: {...formData.primary_contact, email: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                data-testid="client-primary-email-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Teléfono</label>
              <input
                type="tel"
                value={formData.primary_contact.phone}
                onChange={(e) => setFormData({
                  ...formData, 
                  primary_contact: {...formData.primary_contact, phone: e.target.value}
                })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Con Copia Para (C.C.P.) */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-slate-700 pb-2">
            <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Con Copia Para (C.C.P.)
              <span className="text-xs text-slate-500 ml-2">• Aparecerán automáticamente en cotizaciones</span>
            </h4>
            {formData.additional_contacts.length < 5 && (
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  additional_contacts: [
                    ...formData.additional_contacts,
                    { name: '', position: '', email: '', phone: '' }
                  ]
                })}
                className="text-xs px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-300 rounded-md transition-colors flex items-center gap-1"
                data-testid="add-ccp-btn"
              >
                <Plus className="w-3 h-3" /> Agregar copia
              </button>
            )}
          </div>

          {formData.additional_contacts.length === 0 && (
            <p className="text-sm text-slate-500 italic">
              Sin contactos C.C.P. Haz clic en "Agregar copia" si el cliente lo requiere.
            </p>
          )}

          {formData.additional_contacts.map((cc, idx) => (
            <div
              key={idx}
              className="p-3 bg-slate-900/50 border border-slate-700/50 rounded-lg space-y-3"
              data-testid={`ccp-item-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-400 font-medium">C.C.P. #{idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    additional_contacts: formData.additional_contacts.filter((_, i) => i !== idx)
                  })}
                  className="text-red-400 hover:text-red-300 transition-colors"
                  title="Quitar"
                  data-testid={`remove-ccp-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Nombre"
                  value={cc.name || ''}
                  onChange={(e) => {
                    const next = [...formData.additional_contacts];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setFormData({ ...formData, additional_contacts: next });
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Cargo"
                  value={cc.position || ''}
                  onChange={(e) => {
                    const next = [...formData.additional_contacts];
                    next[idx] = { ...next[idx], position: e.target.value };
                    setFormData({ ...formData, additional_contacts: next });
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
                <input
                  type="email"
                  placeholder="Correo"
                  value={cc.email || ''}
                  onChange={(e) => {
                    const next = [...formData.additional_contacts];
                    next[idx] = { ...next[idx], email: e.target.value };
                    setFormData({ ...formData, additional_contacts: next });
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none md:col-span-2"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Notas */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Notas internas</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none resize-none"
            placeholder="Notas o comentarios sobre el cliente..."
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {client?.id ? 'Guardar Cambios' : 'Crear Cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, onEdit, onDelete, onQuote, expanded, onToggle }) => {
  return (
    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="font-medium text-white">{client.business_name}</h4>
              <p className="text-sm text-slate-400">{client.rfc || 'Sin RFC'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onQuote(client); }}
              className="p-2 text-orange-400 hover:bg-orange-500/20 rounded-lg transition-colors"
              title="Nueva cotización"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(client); }}
              className="p-2 text-slate-400 hover:bg-slate-700 rounded-lg transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(client); }}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-700/50 pt-3 space-y-3">
          {client.address?.street && (
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
              <span className="text-slate-300">
                {client.address.street} {client.address.exterior_number}
                {client.address.interior_number && ` Int. ${client.address.interior_number}`}, 
                {client.address.neighborhood && ` ${client.address.neighborhood},`} 
                {client.address.postal_code && ` C.P. ${client.address.postal_code},`} 
                {client.address.city}
              </span>
            </div>
          )}
          {client.primary_contact?.name && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300">{client.primary_contact.name}</span>
            </div>
          )}
          {client.primary_contact?.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300">{client.primary_contact.email}</span>
            </div>
          )}
          {client.primary_contact?.phone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-slate-500" />
              <span className="text-slate-300">{client.primary_contact.phone}</span>
            </div>
          )}
          {client.notes && (
            <div className="text-sm text-slate-400 italic mt-2 pt-2 border-t border-slate-700/50">
              {client.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Clients Tab Component
export default function ClientsTab({ businessId, onCreateQuotation }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchClients = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/clients`);  // Cargar TODOS los clientes
      const data = await res.json();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSave = (client) => {
    if (editingClient) {
      setClients(prev => prev.map(c => c.id === client.id ? client : c));
    } else {
      setClients(prev => [client, ...prev]);
    }
    setShowForm(false);
    setEditingClient(null);
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`¿Eliminar cliente "${client.business_name}"?`)) return;
    
    try {
      const res = await fetch(`${API}/api/clients/${client.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setClients(prev => prev.filter(c => c.id !== client.id));
      }
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const filteredClients = clients.filter(c => 
    c.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.rfc?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clients-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-400" />
            Clientes
          </h2>
          <p className="text-slate-400 mt-1">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''} registrado{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { setEditingClient(null); setShowForm(true); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          data-testid="add-client-btn"
        >
          <Plus className="w-5 h-5" />
          Nuevo Cliente
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por nombre o RFC..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
          data-testid="search-clients"
        />
      </div>

      {/* Form */}
      {showForm && (
        <ClientForm
          client={editingClient}
          businessId={businessId}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingClient(null); }}
        />
      )}

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{search ? 'No se encontraron clientes' : 'No hay clientes registrados'}</p>
          {!search && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-blue-400 hover:text-blue-300"
            >
              Agregar primer cliente
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredClients.map(client => (
            <ClientCard
              key={client.id}
              client={client}
              expanded={expandedId === client.id}
              onToggle={() => setExpandedId(expandedId === client.id ? null : client.id)}
              onEdit={(c) => { setEditingClient(c); setShowForm(true); }}
              onDelete={handleDelete}
              onQuote={(c) => onCreateQuotation && onCreateQuotation(c)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
